import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTable } from '@tanstack/react-table';
import type { FilterFn } from '@tanstack/table-core';
import { Newspaper, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  DataTable,
  type DataTableFeatures,
  DataTablePagination,
  dataTableColumnHelper,
  dataTableDefaultColumn,
  dataTableFeatures,
} from '@/components/data-table';
import { ReadOnlyNotice } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import { createPost, listPosts, type PostRecord, postState, slugify } from '@/lib/blog';

/**
 * Posts.
 *
 * A post is live when it is published AND its date has passed, which is how
 * scheduling works here -- so the badge reads from that pair rather than
 * from `status` alone, and a post dated forward says "Scheduled" instead of
 * claiming to be on the site.
 */
export const Route = createFileRoute('/blog/')({
  component: BlogPage,
});

const STATE_LABEL: Record<string, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  published: 'Live',
};

const date = (value: string | null) =>
  value ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value)) : '—';

const column = dataTableColumnHelper<PostRecord>();

/** Title, slug or tag -- the three things somebody knows about a post. */
const matchesSearch: FilterFn<DataTableFeatures, PostRecord> = (row, _columnId, value) => {
  const term = String(value ?? '')
    .trim()
    .toLowerCase();

  if (!term) return true;

  const { title, slug, tags } = row.original;

  return (
    title.toLowerCase().includes(term) ||
    slug.toLowerCase().includes(term) ||
    tags.some((tag) => tag.toLowerCase().includes(term))
  );
};

const columns = column.columns([
  column.accessor('title', {
    header: 'Title',
    cell: ({ row }) => (
      <div className="min-w-0">
        <Link
          to="/blog/$postId"
          params={{ postId: row.original.id }}
          className="block truncate font-medium hover:underline"
        >
          {row.original.title}
        </Link>
        <div className="text-muted-foreground truncate text-xs">/{row.original.slug}</div>
      </div>
    ),
  }),

  column.accessor((row) => postState(row), {
    id: 'state',
    header: 'Status',
    cell: ({ getValue }) => {
      const state = getValue();

      return (
        <Badge variant={state === 'published' ? 'default' : 'secondary'}>
          {STATE_LABEL[state]}
        </Badge>
      );
    },
  }),

  column.accessor((row) => row.tags.join(', '), {
    id: 'tags',
    header: 'Tags',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground block truncate text-sm">{getValue() || '—'}</span>
    ),
  }),

  column.accessor('publishedAt', {
    header: 'Date',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground text-sm">{date(getValue())}</span>
    ),
  }),

  column.accessor('authorName', {
    header: 'Author',
    cell: ({ getValue }) => (
      <span className="text-muted-foreground block truncate text-sm">{getValue() ?? '—'}</span>
    ),
  }),
]);

function BlogPage() {
  const { activeAccountId, can, session } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');

  const canManage = can('cms.manage');

  const posts = useQuery({
    queryKey: ['posts', accountId],
    queryFn: () => listPosts(accountId),
    enabled: Boolean(accountId),
  });

  const create = useMutation({
    mutationFn: () => {
      const title = 'Untitled post';
      const user = session?.user;

      return createPost(accountId, {
        title,
        // Unique per account, so a second untitled draft needs its own.
        slug: `${slugify(title)}-${Date.now().toString(36)}`,
        excerpt: null,
        body: '',
        coverUrl: null,
        coverMediaId: null,
        tags: [],
        status: 'draft',
        publishedAt: null,
        seo: {},
        authorId: user?.id ?? null,
        authorName:
          (user?.user_metadata?.name as string | undefined) ?? user?.email?.split('@')[0] ?? null,
      });
    },
    onSuccess: (post) => {
      queryClient.invalidateQueries({ queryKey: ['posts', accountId] });
      navigate({ to: '/blog/$postId', params: { postId: post.id } });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const rows = posts.data ?? [];

  const table = useTable({
    features: dataTableFeatures,
    defaultColumn: dataTableDefaultColumn,
    columns,
    data: rows,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    initialState: { pagination: { pageIndex: 0, pageSize: 25 } },
    globalFilterFn: matchesSearch,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Blog</h1>
          <p className="text-muted-foreground text-sm">
            {posts.isLoading ? 'Loading…' : `${rows.length} post${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter by title, slug or tag…"
            value={globalFilter}
            onChange={(event) => setGlobalFilter(event.target.value)}
            className="w-72"
          />

          {canManage && (
            <Button
              type="button"
              size="sm"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              <Plus className="size-4" />
              {create.isPending ? 'Creating…' : 'New post'}
            </Button>
          )}
        </div>
      </div>

      {!canManage && <ReadOnlyNotice permission="cms.manage" />}

      <DataTable
        table={table}
        columnCount={columns.length}
        isLoading={posts.isLoading}
        onRowActivate={(row) => navigate({ to: '/blog/$postId', params: { postId: row.id } })}
        emptyMessage={
          globalFilter ? (
            'No posts match that filter.'
          ) : (
            <span className="flex flex-col items-center gap-2">
              <Newspaper className="size-6" />
              No posts yet.
            </span>
          )
        }
      />

      <DataTablePagination table={table} total={rows.length} />
    </div>
  );
}
